import React from 'react';
import PropTypes from 'prop-types';

import {Box, Chip, Divider, Paper, Typography} from 'views/components';
import {translate} from 'views/module';

import {getParticipantRns} from 'views/scenes/case/communication/communicationUtils';

const formatUpdatedAt = (updatedAt) => {
  const updatedAtDate = new Date(updatedAt);

  if (Number.isNaN(updatedAtDate.getTime())) {
    return '';
  }

  return updatedAtDate.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getPriorityLabel = (caseData) => {
  const priority = caseData.priority;

  if (priority !== null && priority !== undefined && priority !== '') {
    return translate('case.List.PriorityLabel', {priority});
  }

  return translate('case.List.PriorityUnprioritized');
};

const getStatusLabel = (status) => {
  const listStatusLabel = translate(`case.List.StatusDisplay.${status}`);

  return listStatusLabel === 'Missing translation' ?
    translate(`case.Status.${status}.Title`) :
    listStatusLabel;
};

const CaseCard = (props) => {
  const {item} = props;

  const caseData = item;

  const participantLabels = getParticipantRns(caseData)
      .filter((participantRn) => typeof participantRn === 'string' && participantRn);
  const updatedAtLabel = formatUpdatedAt(caseData.updatedAt);
  const priorityLabel = getPriorityLabel(caseData);

  const handleCardClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) {
      return;
    }

    window.open(`/case/${encodeURIComponent(caseData.caseRn)}`, '_blank', 'noopener,noreferrer');
  };

  const handleAuxClick = (event) => {
    if (event.defaultPrevented || event.button !== 1) {
      return;
    }

    event.preventDefault();
    window.open(`/case/${encodeURIComponent(caseData.caseRn)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Paper
      sx={{
        'overflow': 'hidden',
        'p': 2,
        'bgcolor': '#ffffff',
        'width': '100%',
        'opacity': 1,
        'border': 'none',
        'cursor': 'pointer',
        '&:hover': {
          backgroundColor: '#f9f9f9',
        },
      }}
      onClick={handleCardClick}
      onAuxClick={handleAuxClick}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            flex: 1,
            minWidth: 0,
            mr: 1,
            wordBreak: 'break-word',
          }}
        >
          {caseData.caseType}
        </Typography>
        <Chip
          label={priorityLabel}
          size='small'
          sx={{
            'flexShrink': 0,
            'fontWeight': 500,
            '& .MuiChip-label': {
              px: '8px',
            },
          }}
        />
      </Box>

      <Typography sx={{fontSize: 12, marginBottom: 1, wordBreak: 'break-word'}} color="text.secondary">
        {caseData.caseRn}
      </Typography>

      <Divider />

      <Typography sx={{fontSize: 14, marginTop: 1, wordBreak: 'break-word'}} color="text.secondary">
        {participantLabels.join(', ') || translate('case.Detail.EmptyValue')}
      </Typography>

      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 12,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: caseData.status === 'COMPLETE' ? '#008A00' : '#B77900',
          }}
        >
          {getStatusLabel(caseData.status)}
        </Typography>
        <Typography sx={{fontSize: 12}} color="text.secondary">
          {updatedAtLabel}
        </Typography>
      </Box>
    </Paper>
  );
};

CaseCard.propTypes = {
  item: PropTypes.shape({
    caseRn: PropTypes.string.isRequired,
    caseType: PropTypes.string,
    status: PropTypes.string,
    priority: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    updatedAt: PropTypes.string,
  }).isRequired,
};

export default CaseCard;
